import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield, Users, Scale, Flame, Target, ChevronRight, ChevronLeft,
  Check, CheckCircle2, Lock, Loader2, ChevronDown, ChevronUp, RefreshCw, Activity, ScrollText
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth, apiCall } from "../App";
import PurusharthasCard, { PurusharthasAwarenessPair } from "./PurusharthasCard";
import CircleOfOriginCard, { CircleOfOriginAwarenessPair } from "./CircleOfOriginCard";

const API = process.env.REACT_APP_BACKEND_URL;

const PILLAR_COLORS = {
  P1: { bg: "bg-red-500/10", border: "border-red-500/20", text: "text-red-400", accent: "bg-red-500" },
  P2: { bg: "bg-orange-500/10", border: "border-orange-500/20", text: "text-orange-400", accent: "bg-orange-500" },
  P3: { bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-400", accent: "bg-amber-500" },
  P4: { bg: "bg-emerald-500/10", border: "border-emerald-500/20", text: "text-emerald-400", accent: "bg-emerald-500" },
  P5: { bg: "bg-sky-500/10", border: "border-sky-500/20", text: "text-sky-400", accent: "bg-sky-500" },
  P6: { bg: "bg-violet-500/10", border: "border-violet-500/20", text: "text-violet-400", accent: "bg-violet-500" },
};

const QUESTION_GROUPS = [
  {
    id: "pillars", name: "6-PILLAR ASSESSMENT", subtitle: "Core Relationship Patterns",
    description: "Explore the six foundational pillars: delusion traps, attraction-aversion, karma patterns, peace blockers, compassion dynamics, and love delusions.",
    questionIds: ["Q1", "Q2", "Q3A", "Q3B", "Q4", "Q5", "Q6"], IconComp: Shield, color: "text-divine-gold"
  },
  {
    id: "support", name: "SUPPORT NETWORK", subtitle: "Relational Ecosystem",
    description: "Map isolation patterns, mutual ties, and external influences shaping your relationship's support structure.",
    questionIds: ["Q7A", "Q7B", "Q7C"], IconComp: Users, color: "text-sky-400"
  },
  {
    id: "energy", name: "ENERGY POLARITY", subtitle: "Masculine-Feminine Balance",
    description: "Discover how your inner masculine and feminine energies flow, create magnetism, and where they need rebalancing.",
    questionIds: ["Q8"], IconComp: Scale, color: "text-violet-400"
  },
  {
    id: "vikara", name: "VIKARA AWARENESS", subtitle: "Six Inner Disturbances",
    description: "Rate how strongly each inner disturbance shows up in your daily life — Desire, Anger, Greed, Delusion, Envy and Pride.",
    questionIds: ["VK1", "VK2", "VK3", "VK4", "VK5", "VK6"], IconComp: Flame, color: "text-orange-400",
    isSelfAssessment: true
  },
  {
    id: "guna", name: "GUNA DISTRIBUTION", subtitle: "Three Qualities of Nature",
    description: "Assess how the three fundamental energies — Sattva (clarity), Rajas (activity), and Tamas (inertia) — show up in your daily life.",
    questionIds: ["GN1", "GN2", "GN3"], IconComp: Activity, color: "text-teal-400",
    isSelfAssessment: true
  },
  {
    id: "vision", name: "PRAYOJANA VISION", subtitle: "Your Harmony Goal",
    description: "Define your 180-day North Star — the personal transformation you'll commit to for creating harmony.",
    questionIds: ["Q9"], IconComp: Target, color: "text-emerald-400"
  }
];

const VIKARA_LIST = [
  { name: "Kama (Desire)", key: "kama", color: "bg-chakra-root" },
  { name: "Krodha (Anger)", key: "krodha", color: "bg-red-500" },
  { name: "Lobha (Greed)", key: "lobha", color: "bg-amber-500" },
  { name: "Moha (Delusion)", key: "moha", color: "bg-purple-500" },
  { name: "Mada (Pride)", key: "mada", color: "bg-orange-500" },
  { name: "Matsarya (Envy)", key: "matsarya", color: "bg-emerald-500" },
];

// ============ Question Renderer ============

function QuestionInput({ question, answer, onChange }) {
  const q = question;
  const val = answer || {};

  // Helpers for trigger questions: store selected_chips + free_text in answer_options,
  // and serialize them to answer_text so the backend NLP matcher works unchanged.
  const getTriggerSelection = () => val.answer_options?.selected_chips || [];
  const getTriggerFreeText = () => val.answer_options?.free_text ?? val.answer_text ?? "";
  const buildTriggerAnswer = (selected, freeText) => {
    const combined = [...selected, freeText].filter(s => s && String(s).trim()).join(" • ");
    return {
      ...val,
      question_id: q.id,
      answer_text: combined,
      answer_options: { ...(val.answer_options || {}), selected_chips: selected, free_text: freeText },
    };
  };
  const toggleTriggerChip = (chip) => {
    const current = getTriggerSelection();
    const next = current.includes(chip) ? current.filter(s => s !== chip) : [...current, chip];
    onChange(buildTriggerAnswer(next, getTriggerFreeText()));
  };

  if (q.type === "text") {
    const hasSuggestions = Array.isArray(q.suggestions) && q.suggestions.length > 0;
    const selected = getTriggerSelection();
    const freeText = getTriggerFreeText();
    const charLimit = q.max_chars || 100;
    return (
      <div className="space-y-3">
        {hasSuggestions && (
          <>
            <p className="text-chakra-crown/60 text-[11px] leading-relaxed" data-testid={`rd-trigger-instruction-${q.id}`}>
              Select all that apply <span className="text-divine-gold/80">and/or</span> describe in your own words. ({selected.length} selected)
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5" data-testid={`rd-trigger-chips-${q.id}`}>
              {q.suggestions.map((s, i) => {
                const isOn = selected.includes(s);
                return (
                  <button key={i} type="button" onClick={() => toggleTriggerChip(s)}
                    data-testid={`rd-trigger-chip-${q.id}-${i}`}
                    className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-left text-[11px] transition-all ${
                      isOn
                        ? "border-divine-gold bg-divine-gold/15 text-divine-gold"
                        : "border-divine-gold/15 bg-cosmic-deep/30 text-chakra-crown/70 hover:border-divine-gold/40 hover:text-chakra-crown"
                    }`}>
                    <span className={`shrink-0 w-3 h-3 rounded border-[1.5px] flex items-center justify-center ${
                      isOn ? "border-divine-gold bg-divine-gold/20" : "border-divine-gold/40"
                    }`}>
                      {isOn && <span className="block w-1 h-1 rounded-sm bg-divine-gold" />}
                    </span>
                    <span className="leading-snug">{s}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
        <Textarea value={freeText} maxLength={charLimit}
          onChange={e => onChange(buildTriggerAnswer(selected, e.target.value))}
          placeholder={hasSuggestions ? "Describe in your own words (optional)..." : "Share your thoughts..."}
          className="bg-cosmic-deep/50 border-divine-gold/10 text-chakra-crown min-h-[60px]" />
        <div className="flex items-end justify-end">
          <span className="text-[10px] text-chakra-crown/30 whitespace-nowrap">{(freeText || "").length}/{charLimit}</span>
        </div>
        {q.empathy && <p className="text-[10px] text-chakra-crown/30 italic">{q.empathy}</p>}
      </div>
    );
  }

  if (q.type === "scale") {
    return (
      <div className="space-y-3">
        {q.scale_items.map(item => (
          <div key={item} className="flex items-center gap-3">
            <span className="text-chakra-crown/70 text-xs w-20 capitalize">{item.replace(/_/g, " ")}</span>
            <input type="range" min={q.scale_range[0]} max={q.scale_range[1]} step={1}
              value={val.answer_options?.[item] || 0}
              onChange={e => onChange({ question_id: q.id, answer_options: { ...(val.answer_options || {}), [item]: parseInt(e.target.value) } })}
              className="flex-1 h-1.5 bg-cosmic-deep rounded-full appearance-none cursor-pointer accent-divine-gold" />
            <span className="text-divine-gold text-xs w-4 text-right">{val.answer_options?.[item] || 0}</span>
          </div>
        ))}
      </div>
    );
  }

  if (q.type === "dual_scale") {
    const renderGroup = (items, label, accent) => (
      <div>
        <h4 className={`text-xs font-heading mb-2 ${accent}`}>{label}</h4>
        {items.map(item => (
          <div key={item.key} className="flex items-center gap-3 mb-2">
            <span className="text-chakra-crown/70 text-[11px] w-28">{item.label}</span>
            <input type="range" min={0} max={5} step={1}
              value={val.answer_options?.[item.key] || 0}
              onChange={e => onChange({ question_id: q.id, answer_options: { ...(val.answer_options || {}), [item.key]: parseInt(e.target.value) } })}
              className="flex-1 h-1.5 bg-cosmic-deep rounded-full appearance-none cursor-pointer accent-divine-gold" />
            <span className="text-divine-gold text-xs w-4 text-right">{val.answer_options?.[item.key] || 0}</span>
          </div>
        ))}
      </div>
    );
    return (
      <div className="space-y-4">
        {renderGroup(q.masculine_items, "Masculine Qualities", "text-divine-gold")}
        {renderGroup(q.feminine_items, "Feminine Qualities", "text-chakra-heart")}
      </div>
    );
  }

  if (q.type === "text_with_dropdown") {
    const hasSuggestions = Array.isArray(q.suggestions) && q.suggestions.length > 0;
    const selected = getTriggerSelection();
    const freeText = getTriggerFreeText();
    const charLimit = 75;
    return (
      <div className="space-y-3">
        <select value={val.answer_options?.dropdown || ""}
          onChange={e => onChange({ ...val, question_id: q.id, answer_text: val.answer_text || "",
            answer_options: { ...(val.answer_options || {}), dropdown: e.target.value } })}
          className="w-full bg-cosmic-deep/50 border border-divine-gold/10 text-chakra-crown rounded-lg px-3 py-2 text-sm">
          <option value="">Select...</option>
          {q.dropdown_options.map((o, i) => <option key={i} value={o}>{o}</option>)}
        </select>
        {hasSuggestions && (
          <>
            <p className="text-chakra-crown/60 text-[11px] leading-relaxed" data-testid={`rd-twd-instruction-${q.id}`}>
              Select all that apply <span className="text-divine-gold/80">and/or</span> describe in your own words. ({selected.length} selected)
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5" data-testid={`rd-twd-chips-${q.id}`}>
              {q.suggestions.map((s, i) => {
                const isOn = selected.includes(s);
                return (
                  <button key={i} type="button" onClick={() => toggleTriggerChip(s)}
                    data-testid={`rd-twd-chip-${q.id}-${i}`}
                    className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-left text-[11px] transition-all ${
                      isOn
                        ? "border-divine-gold bg-divine-gold/15 text-divine-gold"
                        : "border-divine-gold/15 bg-cosmic-deep/30 text-chakra-crown/70 hover:border-divine-gold/40 hover:text-chakra-crown"
                    }`}>
                    <span className={`shrink-0 w-3 h-3 rounded border-[1.5px] flex items-center justify-center ${
                      isOn ? "border-divine-gold bg-divine-gold/20" : "border-divine-gold/40"
                    }`}>
                      {isOn && <span className="block w-1 h-1 rounded-sm bg-divine-gold" />}
                    </span>
                    <span className="leading-snug">{s}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
        <Textarea value={freeText} maxLength={charLimit}
          onChange={e => onChange(buildTriggerAnswer(selected, e.target.value))}
          placeholder={hasSuggestions ? "Describe in your own words (optional)..." : "Share more details..."}
          className="bg-cosmic-deep/50 border-divine-gold/10 text-chakra-crown min-h-[60px]" />
      </div>
    );
  }

  if (q.type === "dropdown_multi") {
    const selected = val.answer_options?.selected || [];
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          {q.dropdown_options.map((opt, i) => {
            const isSelected = selected.includes(opt);
            return (
              <button key={i} onClick={() => {
                const newSel = isSelected ? selected.filter(s => s !== opt) : [...selected, opt];
                onChange({ question_id: q.id, answer_options: { ...(val.answer_options || {}), selected: newSel } });
              }}
                className={`px-3 py-1.5 rounded-lg border text-xs transition-all ${isSelected ? "bg-divine-gold/10 border-divine-gold text-divine-gold" : "bg-cosmic-light/5 border-divine-gold/10 text-chakra-crown/70 hover:border-divine-gold/30"}`}>
                {opt}
              </button>
            );
          })}
        </div>
        {q.intent_options && (
          <div className="flex gap-2 mt-2">
            {q.intent_options.map((intent, i) => (
              <button key={i} onClick={() => onChange({ question_id: q.id, answer_options: { ...(val.answer_options || {}), intent } })}
                className={`flex-1 p-2 rounded-lg border text-xs ${val.answer_options?.intent === intent ? "bg-divine-gold/10 border-divine-gold text-divine-gold" : "bg-cosmic-light/5 border-divine-gold/10 text-chakra-crown/70"}`}>
                {intent}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return <p className="text-chakra-crown/50 text-sm">Unsupported type: {q.type}</p>;
}

// ============ Collapsible Result Cards ============

function CollapsibleResult({ title, count, countLabel, children, testId, defaultOpen, icon: IconComp, iconColor }) {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <div className="p-4 rounded-xl border border-divine-gold/10 bg-cosmic-light/3" data-testid={testId}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between" data-testid={`${testId}-toggle`}>
        <div className="flex items-center gap-2">
          {IconComp && <IconComp className={`w-4 h-4 ${iconColor || "text-divine-gold/60"}`} />}
          <h4 className="font-heading text-xs text-divine-gold/60">{title}</h4>
        </div>
        <div className="flex items-center gap-2">
          {count !== undefined && <span className="text-[10px] text-chakra-crown/40">{count} {countLabel}</span>}
          {open ? <ChevronUp className="w-3.5 h-3.5 text-divine-gold/40" /> : <ChevronDown className="w-3.5 h-3.5 text-divine-gold/40" />}
        </div>
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

// ============ Full PillarSection with False/Real Perception ============

function PillarTriggerBoard({ triggerBoard }) {
  if (!triggerBoard?.length) return <p className="text-chakra-crown/30 text-sm text-center py-4">Complete the assessment to see triggers</p>;
  return (
    <div className="space-y-3">
      <p className="text-chakra-crown/50 text-xs">Matched triggers grouped by pillar</p>
      {triggerBoard.map(pillar => (
        <PillarExpandable key={pillar.pillar_code} pillar={pillar} />
      ))}
    </div>
  );
}

function PillarExpandable({ pillar }) {
  const [expanded, setExpanded] = useState(false);
  const c = PILLAR_COLORS[pillar.pillar_code] || PILLAR_COLORS.P1;
  return (
    <div className={`rounded-xl border ${c.border} overflow-hidden`} data-testid={`pillar-${pillar.pillar_code}`}>
      <button onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between p-3 ${c.bg} hover:opacity-90 transition-opacity`}
        data-testid={`pillar-toggle-${pillar.pillar_code}`}>
        <div className="flex items-center gap-2">
          <div className={`w-1.5 h-6 rounded-full ${c.accent}`} />
          <div className="text-left">
            <span className={`font-heading text-sm ${c.text}`}>{pillar.pillar_code}: {pillar.pillar_name}</span>
            <span className="text-chakra-crown/50 text-[10px] ml-1">({pillar.pillar_english})</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cosmic-deep text-chakra-crown/70">{pillar.trigger_count} matched</span>
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-chakra-crown/50" /> : <ChevronDown className="w-3.5 h-3.5 text-chakra-crown/50" />}
        </div>
      </button>
      {expanded && (
        <div className="px-3 py-2 space-y-2">
          {pillar.triggers.map(trigger => (
            <div key={trigger.id} className="p-2 rounded-lg bg-cosmic-light/10 border border-divine-gold/5" data-testid={`trigger-card-${trigger.id}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-divine-gold font-medium text-sm">{trigger.name}</span>
                <div className="flex items-center gap-1 text-[10px]">
                  <span className="px-1.5 py-0.5 rounded bg-divine-gold/10 text-divine-gold-light">{trigger.abhideya}</span>
                  <span className="text-chakra-crown/60">{trigger.gita_verse}</span>
                </div>
              </div>
              {trigger.guna_split && (
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex-1 h-1 bg-cosmic-deep rounded-full overflow-hidden flex">
                    <div className="bg-chakra-root h-full" style={{ width: `${trigger.guna_split.rajas}%` }} />
                    <div className="bg-cosmic-light h-full" style={{ width: `${trigger.guna_split.tamas}%` }} />
                    <div className="bg-chakra-heart h-full" style={{ width: `${trigger.guna_split.sattva}%` }} />
                  </div>
                  <span className="text-[10px] text-chakra-crown/50 whitespace-nowrap">{trigger.guna_flow}</span>
                </div>
              )}
              {/* False Perception / Real Perception */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
                <div className="p-2 rounded-md bg-chakra-root/5 border border-chakra-root/10">
                  <span className="text-[9px] text-chakra-root/70 uppercase tracking-wide block mb-1">False Perception</span>
                  <p className="text-chakra-root text-xs font-medium leading-relaxed whitespace-pre-line"
                    data-testid={`awareness-false-${trigger.id}`}>
                    {trigger.false_perception}
                  </p>
                </div>
                <div className="p-2 rounded-md bg-chakra-heart/5 border border-chakra-heart/10">
                  <span className="text-[9px] text-chakra-heart/70 uppercase tracking-wide block mb-1">Real Perception</span>
                  <p className="text-chakra-heart text-xs font-medium leading-relaxed whitespace-pre-line"
                    data-testid={`awareness-real-${trigger.id}`}>
                    {trigger.real_perception}
                  </p>
                </div>
              </div>
              {/* Wisdom Heading + Text (admin-editable) */}
              {(trigger.wisdom_heading || trigger.wisdom_text) && (
                <div className="mt-2 p-2 rounded-md bg-divine-gold/5 border border-divine-gold/15"
                  data-testid={`awareness-wisdom-${trigger.id}`}>
                  {trigger.wisdom_heading && (
                    <span className="text-[9px] text-divine-gold/80 uppercase tracking-wider block mb-0.5 font-heading"
                      data-testid={`awareness-wisdom-heading-${trigger.id}`}>
                      Wisdom · {trigger.wisdom_heading}
                    </span>
                  )}
                  {trigger.wisdom_text && (
                    <span className="text-chakra-crown/70 text-xs italic leading-relaxed block"
                      data-testid={`awareness-wisdom-text-${trigger.id}`}>
                      {trigger.wisdom_text}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EnergyResult({ energy, selfAssessment }) {
  if (!energy?.masculine) return <p className="text-chakra-crown/30 text-sm text-center py-4">Complete Energy Polarity to see results</p>;
  const hasSelfData = selfAssessment && (Object.keys(selfAssessment.masculine || {}).length > 0 || Object.keys(selfAssessment.feminine || {}).length > 0);
  return (
    <div className="space-y-4">
      {/* Pillar-Based Weighted Matrix */}
      <div>
        <p className="label-sacred text-xs mb-2">6-PILLAR WEIGHTED MATRIX</p>
        <div className="space-y-2">
          {[
            { label: "Masculine", value: energy.masculine, color: "bg-divine-gold" },
            { label: "Feminine", value: energy.feminine, color: "bg-chakra-heart" },
            { label: "Harmony", value: energy.harmony_potential, color: "bg-emerald-400" },
          ].map(item => (
            <div key={item.label}>
              <div className="flex justify-between mb-1">
                <span className="text-chakra-crown/70 text-xs">{item.label}</span>
                <span className="text-divine-gold text-xs">{(item.value * 100).toFixed(0)}%</span>
              </div>
              <div className="h-1.5 bg-cosmic-deep rounded-full overflow-hidden">
                <div className={`h-full ${item.color} transition-all`} style={{ width: `${item.value * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Self-Assessment (Q8) */}
      {hasSelfData && (
        <div className="pt-3 border-t border-divine-gold/10">
          <p className="label-sacred text-xs mb-2">SELF-ASSESSMENT</p>
          <div className="grid grid-cols-2 gap-3">
            {Object.keys(selfAssessment.masculine || {}).length > 0 && (
              <div>
                <p className="text-sky-400 text-xs font-medium mb-1.5">MASCULINE</p>
                {Object.entries(selfAssessment.masculine).map(([k, v]) => (
                  <div key={k} className="mb-1.5">
                    <div className="flex justify-between text-xs"><span className="text-chakra-crown/60 capitalize">{k.replace(/_/g, ' ')}</span><span className="text-sky-400">{v}/5</span></div>
                    <div className="h-1.5 bg-cosmic-deep rounded-full"><div className="h-full bg-sky-500 rounded-full" style={{ width: `${(v / 5) * 100}%` }} /></div>
                  </div>
                ))}
              </div>
            )}
            {Object.keys(selfAssessment.feminine || {}).length > 0 && (
              <div>
                <p className="text-pink-400 text-xs font-medium mb-1.5">FEMININE</p>
                {Object.entries(selfAssessment.feminine).map(([k, v]) => (
                  <div key={k} className="mb-1.5">
                    <div className="flex justify-between text-xs"><span className="text-chakra-crown/60 capitalize">{k.replace(/_/g, ' ')}</span><span className="text-pink-400">{v}/5</span></div>
                    <div className="h-1.5 bg-cosmic-deep rounded-full"><div className="h-full bg-pink-500 rounded-full" style={{ width: `${(v / 5) * 100}%` }} /></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {!hasSelfData && (
        <div className="pt-3 border-t border-divine-gold/10">
          <p className="label-sacred text-xs mb-1">SELF-ASSESSMENT</p>
          <p className="text-chakra-crown/40 text-xs">Retake the Energy Polarity quiz to record self-assessment details</p>
        </div>
      )}
    </div>
  );
}

function VikaraResult({ vikara, selfAssessment }) {
  if (!vikara?.kama && !selfAssessment) return <p className="text-chakra-crown/30 text-sm text-center py-4">Complete profiling or Vikara self-assessment to see results</p>;
  const hasPillarData = vikara && vikara.kama !== undefined;
  const hasSelfData = selfAssessment && Object.keys(selfAssessment).length > 0;
  return (
    <div className="space-y-4">
      {/* Pillar-Based Weighted Matrix */}
      {hasPillarData && (
        <div>
          <p className="label-sacred text-xs mb-2">6-PILLAR WEIGHTED MATRIX</p>
          <div className="space-y-2">
            {VIKARA_LIST.map(v => (
              <div key={v.key}>
                <div className="flex justify-between mb-0.5">
                  <span className="text-chakra-crown/70 text-[11px]">{v.name}</span>
                  <span className="text-divine-gold text-[11px]">{vikara[v.key]?.toFixed(1)}/10</span>
                </div>
                <div className="h-1 bg-cosmic-deep rounded-full overflow-hidden">
                  <div className={`h-full ${v.color} transition-all`} style={{ width: `${(vikara[v.key] || 0) * 10}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Self-Assessment */}
      {hasSelfData && (
        <div className={hasPillarData ? "pt-3 border-t border-divine-gold/10" : ""}>
          <p className="label-sacred text-xs mb-2">SELF-ASSESSMENT</p>
          <div className="space-y-2">
            {VIKARA_LIST.map(v => {
              const score = selfAssessment[v.key];
              if (score === undefined || score === null) return null;
              return (
                <div key={v.key}>
                  <div className="flex justify-between mb-0.5">
                    <span className="text-chakra-crown/60 text-[11px]">{v.name}</span>
                    <span className="text-sky-400 text-[11px]">{score}/10</span>
                  </div>
                  <div className="h-1 bg-cosmic-deep rounded-full overflow-hidden">
                    <div className="h-full bg-sky-500 rounded-full transition-all" style={{ width: `${score * 10}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {!hasSelfData && hasPillarData && (
        <div className="pt-3 border-t border-divine-gold/10">
          <p className="label-sacred text-xs mb-1">SELF-ASSESSMENT</p>
          <p className="text-chakra-crown/40 text-xs">Take the Vikara Awareness quiz for self-assessment scores</p>
        </div>
      )}
    </div>
  );
}

const GUNA_LIST = [
  { name: "Sattva", key: "sattva", color: "bg-chakra-heart", selfColor: "bg-emerald-400" },
  { name: "Rajas", key: "rajas", color: "bg-chakra-root", selfColor: "bg-rose-400" },
  { name: "Tamas", key: "tamas", color: "bg-cosmic-light", selfColor: "bg-purple-400" },
];

function PrayojanaVisionResult({ answer }) {
  const selectedChips = answer?.answer_options?.selected_chips || [];
  const freeText = answer?.answer_options?.free_text || answer?.answer_text || "";
  const hasData = selectedChips.length > 0 || freeText.trim().length > 0;
  if (!hasData) {
    return <p className="text-chakra-crown/50 text-sm text-center py-4">Complete the Prayojana Vision question to see your 180-day North Star</p>;
  }
  return (
    <div className="space-y-3" data-testid="prayojana-vision-result">
      {selectedChips.length > 0 && (
        <div>
          <p className="label-sacred text-xs mb-2">SELECTED GROWTH AREAS</p>
          <div className="flex flex-wrap gap-1.5">
            {selectedChips.map((chip, i) => (
              <span key={i} data-testid={`prayojana-chip-${i}`}
                className="px-2 py-1 rounded-md text-[11px] bg-emerald-500/10 text-emerald-300 border border-emerald-400/30">
                {chip}
              </span>
            ))}
          </div>
        </div>
      )}
      {freeText && (
        <div className={selectedChips.length > 0 ? "pt-3 border-t border-divine-gold/10" : ""}>
          <p className="label-sacred text-xs mb-2">YOUR 180-DAY NORTH STAR</p>
          <p className="text-chakra-crown/80 text-sm leading-relaxed italic whitespace-pre-wrap" data-testid="prayojana-free-text">
            &ldquo;{freeText}&rdquo;
          </p>
        </div>
      )}
      <p className="text-[10px] text-emerald-300/50 italic pt-1">Harmony blossoms within. Revisit this North Star weekly.</p>
    </div>
  );
}

function GunaResult({ guna, selfAssessment }) {
  const hasPillarData = guna && guna.rajas !== undefined;
  const hasSelfData = selfAssessment && Object.keys(selfAssessment).length > 0;
  if (!hasPillarData && !hasSelfData) return <p className="text-chakra-crown/30 text-sm text-center py-4">Complete profiling or Guna self-assessment to see results</p>;
  return (
    <div className="space-y-4">
      {/* Pillar-Based Distribution */}
      {hasPillarData && (
        <div>
          <p className="label-sacred text-xs mb-2">6-PILLAR WEIGHTED MATRIX</p>
          <div className="space-y-3">
            {GUNA_LIST.map(g => (
              <div key={g.key}>
                <div className="flex justify-between mb-1">
                  <span className="text-chakra-crown/70 text-xs">{g.name}</span>
                  <span className="text-divine-gold text-xs">{guna[g.key]?.toFixed(0)}%</span>
                </div>
                <div className="h-1.5 bg-cosmic-deep rounded-full overflow-hidden">
                  <div className={`h-full ${g.color} transition-all`} style={{ width: `${guna[g.key]}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Self-Assessment */}
      {hasSelfData && (
        <div className={hasPillarData ? "pt-3 border-t border-divine-gold/10" : ""}>
          <p className="label-sacred text-xs mb-2">SELF-ASSESSMENT</p>
          <div className="space-y-3">
            {GUNA_LIST.map(g => {
              const score = selfAssessment[g.key];
              if (score === undefined || score === null) return null;
              return (
                <div key={g.key}>
                  <div className="flex justify-between mb-1">
                    <span className="text-chakra-crown/60 text-xs">{g.name}</span>
                    <span className="text-sky-400 text-xs">{score}/10</span>
                  </div>
                  <div className="h-1.5 bg-cosmic-deep rounded-full overflow-hidden">
                    <div className={`h-full ${g.selfColor} rounded-full transition-all`} style={{ width: `${score * 10}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {!hasSelfData && hasPillarData && (
        <div className="pt-3 border-t border-divine-gold/10">
          <p className="label-sacred text-xs mb-1">SELF-ASSESSMENT</p>
          <p className="text-chakra-crown/40 text-xs">Take the Guna Distribution quiz for self-assessment scores</p>
        </div>
      )}
    </div>
  );
}

const RISK_COLORS = {
  LOW: { bar: "bg-emerald-400", badge: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20" },
  MODERATE: { bar: "bg-amber-400", badge: "bg-amber-500/15 text-amber-400 border-amber-500/20" },
  ELEVATED: { bar: "bg-orange-400", badge: "bg-orange-500/15 text-orange-400 border-orange-500/20" },
  HIGH: { bar: "bg-red-400", badge: "bg-red-500/15 text-red-400 border-red-500/20" },
  CRITICAL: { bar: "bg-red-500", badge: "bg-red-600/20 text-red-300 border-red-500/30" },
  WEAK: { bar: "bg-red-400", badge: "bg-red-500/15 text-red-400 border-red-500/20" },
  FRAGILE: { bar: "bg-orange-400", badge: "bg-orange-500/15 text-orange-400 border-orange-500/20" },
  DEVELOPING: { bar: "bg-amber-400", badge: "bg-amber-500/15 text-amber-400 border-amber-500/20" },
  HEALTHY: { bar: "bg-emerald-400", badge: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20" },
  STRONG: { bar: "bg-emerald-500", badge: "bg-emerald-600/20 text-emerald-300 border-emerald-500/30" },
};

function SupportNetworkResult({ analysis }) {
  if (!analysis || !analysis.dimensions) return <p className="text-chakra-crown/30 text-sm text-center py-4">Complete Support Network section to see results</p>;
  const totalColor = RISK_COLORS[analysis.total_label] || RISK_COLORS.MODERATE;
  return (
    <div className="space-y-4" data-testid="support-network-result">
      {/* Total Risk Header */}
      <div className="rounded-lg bg-cosmic-deep/60 border border-divine-gold/10 p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-chakra-crown/60 text-xs font-medium tracking-wider uppercase">Relational Gravity</span>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${totalColor.badge}`}>{analysis.total_label}</span>
        </div>
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-2xl font-heading text-divine-gold">{analysis.total_risk}%</span>
          <span className="text-chakra-crown/40 text-xs">risk score</span>
        </div>
        <div className="h-2 bg-cosmic-void rounded-full overflow-hidden">
          <div className={`h-full ${totalColor.bar} rounded-full transition-all duration-700`} style={{ width: `${analysis.total_risk}%` }} />
        </div>
        <p className="text-chakra-crown/30 text-[10px] mt-1.5">
          Formula: (Q1 x 0.4) + (Q2 x 0.3) + (Q3 x 0.3)
        </p>
      </div>

      {/* Individual Dimensions */}
      <div className="space-y-3">
        {analysis.dimensions.map(dim => {
          const c = RISK_COLORS[dim.label] || RISK_COLORS.MODERATE;
          return (
            <div key={dim.key} className="group" data-testid={`support-dim-${dim.key}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-chakra-crown/80 text-xs font-medium">{dim.name}</span>
                  <span className="text-chakra-crown/30 text-[10px]">(x{dim.weight})</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-divine-gold text-xs font-medium">{dim.score}%</span>
                  <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${c.badge}`}>{dim.label}</span>
                </div>
              </div>
              <div className="h-1.5 bg-cosmic-void rounded-full overflow-hidden">
                <div className={`h-full ${c.bar} rounded-full transition-all duration-700`} style={{ width: `${dim.score}%` }} />
              </div>
              <p className="text-chakra-crown/25 text-[10px] mt-0.5">{dim.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ Main Component ============

export default function RelationshipDiagnostic({ token, userId, awareness, onRetakeAll: onRetakeAllCallback, extraCompleted = 0, extraTotal = 0 }) {
  const containerRef = useRef(null);
  const [allQuestions, setAllQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [completedSections, setCompletedSections] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [quizView, setQuizView] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [retakenSections, setRetakenSections] = useState(new Set());

  // Vikara Self-Assessment state
  const [vikaraQuestions, setVikaraQuestions] = useState([]);
  const [vikaraOptions, setVikaraOptions] = useState([]);
  const [vikaraAnswers, setVikaraAnswers] = useState({});
  const [vikaraCompleted, setVikaraCompleted] = useState(false);

  // Guna Self-Assessment state
  const [gunaQuestions, setGunaQuestions] = useState([]);
  const [gunaOptions, setGunaOptions] = useState([]);
  const [gunaAnswers, setGunaAnswers] = useState({});
  const [gunaCompleted, setGunaCompleted] = useState(false);

  // Coach Assessment Matrix status (for Awareness section CTA — always visible)
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [coachMatrixActive, setCoachMatrixActive] = useState(false);
  const isOwnClient = authUser?.role === "client" && (!userId || userId === authUser?.user_id);
  const isCoachOrAdmin = authUser?.role === "coach" || authUser?.role === "admin";
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      // Client → /me endpoint (their own status). Coach/admin viewing client → matrix/user/{userId}
      if (isOwnClient) {
        const res = await apiCall("GET", "/coach-assessment-orders/status", null, token);
        if (!cancelled && res.success) setCoachMatrixActive(!!res.data.active);
      } else if (isCoachOrAdmin && userId) {
        const res = await apiCall("GET", `/coach-assessment-matrix/user/${userId}`, null, token);
        if (!cancelled && res.success) setCoachMatrixActive(!!res.data.active);
      }
    })();
    return () => { cancelled = true; };
  }, [isOwnClient, isCoachOrAdmin, userId, token]);

  // profilingComplete = fully done (trigger_board populated AND not preliminary).
  // This preserves the "all sections done" gate for section-taken UI markers while
  // still allowing the trigger_board / trinity_gap to render live during partial saves.
  const profilingComplete = awareness?.trigger_board?.length > 0 && !awareness?.is_preliminary;
  const isPreliminary = awareness?.is_preliminary === true;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const userIdParam = userId ? `?user_id=${userId}` : '';
      const [qRes, pRes, vqRes, vrRes, gqRes, grRes] = await Promise.all([
        fetch(`${API}/api/profiling/questions`, { headers }),
        fetch(`${API}/api/profiling/partial`, { headers }),
        fetch(`${API}/api/vikara-assessment/questions`, { headers }),
        fetch(`${API}/api/vikara-assessment/results${userIdParam}`, { headers }),
        fetch(`${API}/api/guna-assessment/questions`, { headers }),
        fetch(`${API}/api/guna-assessment/results${userIdParam}`, { headers }),
      ]);
      const qData = await qRes.json();
      const pData = await pRes.json();
      setAllQuestions(qData.questions || []);

      // Vikara self-assessment data
      const vqData = await vqRes.json();
      setVikaraQuestions(vqData.questions || []);
      setVikaraOptions(vqData.options || []);
      const vrData = await vrRes.json();
      if (vrData.completed) {
        setVikaraAnswers(vrData.answers || {});
        setVikaraCompleted(true);
      }

      // Guna self-assessment data
      const gqData = await gqRes.json();
      setGunaQuestions(gqData.questions || []);
      setGunaOptions(gqData.options || []);
      const grData = await grRes.json();
      if (grData.completed) {
        setGunaAnswers(grData.answers || {});
        setGunaCompleted(true);
      }

      if (pData.completed) {
        const m = {};
        if (pData.answers?.length) {
          pData.answers.forEach(a => { m[a.question_id] = a; });
        }
        if (pData.sections) {
          Object.values(pData.sections).forEach(sarr => {
            sarr.forEach(a => { m[a.question_id] = a; });
          });
        }
        setAnswers(m);
        setCompletedSections(QUESTION_GROUPS.filter(g => !g.infoOnly && !g.isSelfAssessment).map(g => g.id));
      } else if (pData.sections && Object.keys(pData.sections).length > 0) {
        const m = {};
        const done = [];
        Object.entries(pData.sections).forEach(([sid, sarr]) => {
          sarr.forEach(a => { m[a.question_id] = a; });
          done.push(sid);
        });
        setAnswers(m);
        setCompletedSections(done);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateAnswer = (qid, answerObj) => {
    setAnswers(prev => ({ ...prev, [qid]: answerObj }));
  };

  const getGroupQuestions = (group) => {
    return group.questionIds.map(qid => allQuestions.find(q => q.id === qid)).filter(Boolean);
  };

  const isSectionAnswered = (group) => {
    if (group.infoOnly) return true;
    if (group.isSelfAssessment && group.id === "vikara") {
      return vikaraQuestions.length > 0 && vikaraQuestions.every(q => vikaraAnswers[q.vikara] !== undefined);
    }
    if (group.isSelfAssessment && group.id === "guna") {
      return gunaQuestions.length > 0 && gunaQuestions.every(q => gunaAnswers[q.guna] !== undefined);
    }
    return group.questionIds.every(qid => {
      const a = answers[qid];
      if (!a) return false;
      return a.answer_text || a.answer_value !== undefined || (a.answer_options && Object.keys(a.answer_options).length > 0);
    });
  };

  const saveSection = async (sectionId) => {
    const group = QUESTION_GROUPS.find(g => g.id === sectionId);
    if (!group) return;
    setSaving(true);
    const sectionAnswers = group.questionIds.map(qid => answers[qid]).filter(Boolean);
    try {
      const res = await fetch(`${API}/api/profiling/save-section`, {
        method: "POST", headers,
        body: JSON.stringify({ section_id: sectionId, answers: sectionAnswers }),
      });
      if (!res.ok) {
        const err = await res.text();
        console.error("Profiling save-section failed:", res.status, err);
        toast.error(`Save failed (${res.status}). Please retry.`);
        setSaving(false);
        return;
      }
      const data = await res.json();
      if (!completedSections.includes(sectionId)) {
        setCompletedSections(prev => [...prev, sectionId]);
      }
      // Clear retaken flag since we just saved
      setRetakenSections(prev => { const next = new Set(prev); next.delete(sectionId); return next; });

      // ── ITER 142: Immediate feedback ─────────────────────────────────────
      // After EVERY section save (partial or full), compute preliminary scores so the
      // Trinity Gap + false/real perception (triggers) refresh in the result panels
      // right away — no more "wait until all sections done" gate. The is_partial flag
      // ensures `profiling_complete=true` is only flipped on the truly-final save.
      const allAnswers = [];
      QUESTION_GROUPS.forEach(g => {
        g.questionIds.forEach(qid => { if (answers[qid]) allAnswers.push(answers[qid]); });
      });
      const submitRes = await fetch(`${API}/api/profiling/submit`, {
        method: "POST", headers,
        body: JSON.stringify({ answers: allAnswers, is_partial: !data.all_sections_complete }),
      });
      if (!submitRes.ok) {
        const err = await submitRes.text();
        console.error("Profiling submit failed:", submitRes.status, err);
        toast.error(data.all_sections_complete
          ? "All sections saved, but score calculation failed. Please contact support."
          : "Section saved, but preliminary score refresh failed."
        );
        setSaving(false);
        return;
      }

      if (data.all_sections_complete) {
        toast.success("12D Profiling Complete! Scores recalculated.");
        setTimeout(() => window.location.reload(), 1500);
      } else {
        toast.success(`${group.name} saved — preliminary results updated below`);
        // Reload just enough to refresh the result panels without losing scroll position
        setTimeout(() => window.location.reload(), 800);
      }
      setQuizView("overview");
      setTimeout(() => containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e) { console.error(e); toast.error("Save failed"); }
    setSaving(false);
  };

  const handleRetakeSection = async (sectionId) => {
    setSaving(true);
    try {
      await fetch(`${API}/api/profiling/retake-section`, {
        method: "POST", headers,
        body: JSON.stringify({ section_id: sectionId }),
      });
      // Clear local answers for this section
      const group = QUESTION_GROUPS.find(g => g.id === sectionId);
      if (group) {
        const newAnswers = { ...answers };
        group.questionIds.forEach(qid => delete newAnswers[qid]);
        setAnswers(newAnswers);
      }
      setCompletedSections(prev => prev.filter(s => s !== sectionId));
      // Mark this section as retaken so result card shows placeholder
      setRetakenSections(prev => new Set([...prev, sectionId]));
      setActiveSection(sectionId);
      setQuizView("quiz");
      toast.success(`${group?.name || sectionId} reset. Fill in your new answers.`);
      // Notify parent to refresh downstream data (Alignment/Awakening/Harmony cleared on backend)
      if (onRetakeAllCallback) onRetakeAllCallback();
      setTimeout(() => containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e) { console.error(e); toast.error("Retake failed"); }
    setSaving(false);
  };

  const handleRetakeAll = async () => {
    if (!window.confirm("This will reset ALL your 12D profiling results and clear your Alignment, Awakening & Harmony progress. Continue?")) return;
    setSaving(true);
    try {
      await fetch(`${API}/api/profiling/retake-all-sections`, { method: "POST", headers });
      setAnswers({});
      setCompletedSections([]);
      setRetakenSections(new Set(QUESTION_GROUPS.filter(g => !g.infoOnly && !g.isSelfAssessment).map(g => g.id)));
      toast.success("Full profiling and downstream progress reset. Retake all sections to rebuild your journey.");
      if (onRetakeAllCallback) onRetakeAllCallback();
    } catch (e) { console.error(e); toast.error("Reset failed"); }
    setSaving(false);
  };

  // ─── Vikara Self-Assessment Handlers ───
  const saveVikaraAssessment = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/vikara-assessment/save`, {
        method: "POST", headers,
        body: JSON.stringify({ answers: vikaraAnswers }),
      });
      if (res.ok) {
        setVikaraCompleted(true);
        toast.success("Vikara Self-Assessment saved!");
        setQuizView("overview");
        setTimeout(() => containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
        // Trigger parent refresh so awareness data re-fetches
        if (onRetakeAllCallback) onRetakeAllCallback();
      } else {
        const err = await res.text();
        console.error("Vikara save failed:", res.status, err);
        toast.error(`Save failed (${res.status}). Please retry or contact support.`);
      }
    } catch (e) {
      console.error(e);
      toast.error("Network error. Please retry.");
    }
    setSaving(false);
  };

  const retakeVikaraAssessment = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/vikara-assessment/retake`, { method: "POST", headers });
      setVikaraAnswers({});
      setVikaraCompleted(false);
      setActiveSection("vikara");
      setQuizView("vikara-quiz");
      toast.success("Vikara Self-Assessment reset. Fill in your new answers.");
      setTimeout(() => containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e) { console.error(e); toast.error("Retake failed"); }
    setSaving(false);
  };

  // ─── Guna Self-Assessment Handlers ───
  const saveGunaAssessment = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/guna-assessment/save`, {
        method: "POST", headers,
        body: JSON.stringify({ answers: gunaAnswers }),
      });
      if (res.ok) {
        setGunaCompleted(true);
        toast.success("Guna Self-Assessment saved!");
        setQuizView("overview");
        setTimeout(() => containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
        if (onRetakeAllCallback) onRetakeAllCallback();
      } else {
        const err = await res.text();
        console.error("Guna save failed:", res.status, err);
        toast.error(`Save failed (${res.status}). Please retry or contact support.`);
      }
    } catch (e) {
      console.error(e);
      toast.error("Network error. Please retry.");
    }
    setSaving(false);
  };

  const retakeGunaAssessment = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/guna-assessment/retake`, { method: "POST", headers });
      setGunaAnswers({});
      setGunaCompleted(false);
      setActiveSection("guna");
      setQuizView("guna-quiz");
      toast.success("Guna Self-Assessment reset. Fill in your new answers.");
      setTimeout(() => containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e) { console.error(e); toast.error("Retake failed"); }
    setSaving(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-divine-gold animate-spin" /></div>;
  }

  const activeGroup = QUESTION_GROUPS.find(g => g.id === activeSection);
  const activeQuestions = activeGroup ? getGroupQuestions(activeGroup) : [];
  const reqSections = QUESTION_GROUPS.filter(g => !g.infoOnly);
  const completedCount = reqSections.filter(g => {
    if (g.isSelfAssessment && g.id === "vikara") return vikaraCompleted;
    if (g.isSelfAssessment && g.id === "guna") return gunaCompleted;
    return completedSections.includes(g.id) || (profilingComplete && !g.infoOnly && !g.isSelfAssessment);
  }).length;
  // Include external tests (Tatkalik, Issue Diagnosis, Kosha Quiz) in the total count
  const totalTestCount = reqSections.length + extraTotal;
  const totalCompletedCount = completedCount + extraCompleted;

  // ====== VIKARA SELF-ASSESSMENT QUIZ VIEW ======
  if (quizView === "vikara-quiz") {
    const vikaraAllAnswered = vikaraQuestions.length > 0 && vikaraQuestions.every(q => vikaraAnswers[q.vikara] !== undefined);
    return (
      <div className="space-y-5" ref={containerRef} data-testid="vikara-assessment-quiz">
        <button onClick={() => { setQuizView("overview"); setTimeout(() => containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }}
          className="flex items-center gap-2 text-sm text-chakra-crown/50 hover:text-divine-gold transition-colors" data-testid="exit-vikara-quiz-btn">
          <ChevronLeft className="w-4 h-4" /> Back to All Sections
        </button>

        <AnimatePresence mode="wait">
          <motion.div key="vikara-sa" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
            <div className="sacred-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center shrink-0">
                  <Flame className="w-6 h-6 text-orange-400" />
                </div>
                <div>
                  <h3 className="font-heading text-lg text-orange-400">VIKARA AWARENESS</h3>
                  <p className="text-sm text-chakra-crown/40 font-heading">Six Inner Disturbances</p>
                  <p className="text-sm text-chakra-crown/60 mt-2 leading-relaxed">
                    Rate how strongly each inner disturbance shows up in your daily life. Be honest — there are no right or wrong answers.
                  </p>
                </div>
              </div>
            </div>

            {vikaraQuestions.map((q, idx) => (
              <div key={q.id} className="sacred-card" data-testid={`vikara-question-${q.id}`}>
                <div className="flex items-start gap-3 mb-4">
                  <span className="text-orange-400/40 text-sm font-heading shrink-0 mt-0.5">Q{idx + 1}</span>
                  <div>
                    <p className="text-chakra-crown text-sm leading-relaxed">{q.question}</p>
                    <span className="text-orange-300/40 text-[10px]">{q.vikara_label}</span>
                  </div>
                </div>
                <div className="space-y-2 ml-7">
                  {vikaraOptions.map(opt => {
                    const selected = vikaraAnswers[q.vikara] === opt.value;
                    return (
                      <button key={opt.value}
                        onClick={() => setVikaraAnswers(prev => ({ ...prev, [q.vikara]: opt.value }))}
                        className={`w-full text-left px-4 py-2.5 rounded-xl border transition-all text-sm ${
                          selected
                            ? "bg-orange-500/15 border-orange-500/40 text-orange-300"
                            : "bg-cosmic-deep/30 border-divine-gold/10 text-chakra-crown/60 hover:border-divine-gold/20 hover:bg-cosmic-deep/50"
                        }`}
                        data-testid={`vikara-opt-${q.vikara}-${opt.value}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                            selected ? "border-orange-400" : "border-divine-gold/20"
                          }`}>
                            {selected && <div className="w-2 h-2 rounded-full bg-orange-400" />}
                          </div>
                          <span>{opt.label}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-chakra-crown/40">
                {Object.keys(vikaraAnswers).length}/{vikaraQuestions.length} answered
              </span>
              <div className="flex gap-3">
                <button onClick={() => { setQuizView("overview"); setTimeout(() => containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-divine-gold/20 text-chakra-crown/60 hover:text-divine-gold hover:border-divine-gold/40 text-sm transition-all"
                  data-testid="exit-vikara-quiz-bottom-btn">
                  Exit
                </button>
                <button onClick={saveVikaraAssessment} disabled={saving || !vikaraAllAnswered}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-orange-500 text-cosmic-void font-heading text-sm hover:bg-orange-400 disabled:opacity-50 transition-all"
                  data-testid="save-vikara-assessment-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  SAVE ASSESSMENT
                </button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  // ====== GUNA SELF-ASSESSMENT QUIZ VIEW ======
  if (quizView === "guna-quiz") {
    const gunaAllAnswered = gunaQuestions.length > 0 && gunaQuestions.every(q => gunaAnswers[q.guna] !== undefined);
    return (
      <div className="space-y-5" ref={containerRef} data-testid="guna-assessment-quiz">
        <button onClick={() => { setQuizView("overview"); setTimeout(() => containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }}
          className="flex items-center gap-2 text-sm text-chakra-crown/50 hover:text-divine-gold transition-colors" data-testid="exit-guna-quiz-btn">
          <ChevronLeft className="w-4 h-4" /> Back to All Sections
        </button>

        <AnimatePresence mode="wait">
          <motion.div key="guna-sa" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
            <div className="sacred-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center shrink-0">
                  <Activity className="w-6 h-6 text-teal-400" />
                </div>
                <div>
                  <h3 className="font-heading text-lg text-teal-400">GUNA DISTRIBUTION</h3>
                  <p className="text-sm text-chakra-crown/40 font-heading">Three Qualities of Nature</p>
                  <p className="text-sm text-chakra-crown/60 mt-2 leading-relaxed">
                    Assess how the three fundamental energies show up in your daily life. Be honest — there are no right or wrong answers.
                  </p>
                </div>
              </div>
            </div>

            {gunaQuestions.map((q, idx) => (
              <div key={q.id} className="sacred-card" data-testid={`guna-question-${q.id}`}>
                <div className="flex items-start gap-3 mb-4">
                  <span className="text-teal-400/40 text-sm font-heading shrink-0 mt-0.5">Q{idx + 1}</span>
                  <div>
                    <p className="text-chakra-crown text-sm leading-relaxed">{q.question}</p>
                    <span className="text-teal-300/40 text-[10px]">{q.guna_label}</span>
                  </div>
                </div>
                <div className="space-y-2 ml-7">
                  {gunaOptions.map(opt => {
                    const selected = gunaAnswers[q.guna] === opt.value;
                    return (
                      <button key={opt.value}
                        onClick={() => setGunaAnswers(prev => ({ ...prev, [q.guna]: opt.value }))}
                        className={`w-full text-left px-4 py-2.5 rounded-xl border transition-all text-sm ${
                          selected
                            ? "bg-teal-500/15 border-teal-500/40 text-teal-300"
                            : "bg-cosmic-deep/30 border-divine-gold/10 text-chakra-crown/60 hover:border-divine-gold/20 hover:bg-cosmic-deep/50"
                        }`}
                        data-testid={`guna-opt-${q.guna}-${opt.value}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                            selected ? "border-teal-400" : "border-divine-gold/20"
                          }`}>
                            {selected && <div className="w-2 h-2 rounded-full bg-teal-400" />}
                          </div>
                          <span>{opt.label}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-chakra-crown/40">
                {Object.keys(gunaAnswers).length}/{gunaQuestions.length} answered
              </span>
              <div className="flex gap-3">
                <button onClick={() => { setQuizView("overview"); setTimeout(() => containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-divine-gold/20 text-chakra-crown/60 hover:text-divine-gold hover:border-divine-gold/40 text-sm transition-all"
                  data-testid="exit-guna-quiz-bottom-btn">
                  Exit
                </button>
                <button onClick={saveGunaAssessment} disabled={saving || !gunaAllAnswered}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-teal-500 text-cosmic-void font-heading text-sm hover:bg-teal-400 disabled:opacity-50 transition-all"
                  data-testid="save-guna-assessment-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  SAVE ASSESSMENT
                </button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  // ====== QUIZ VIEW ======
  if (quizView === "quiz" && activeGroup) {
    return (
      <div className="space-y-5" ref={containerRef} data-testid="rel-diagnostic-quiz">
        <button onClick={() => { setQuizView("overview"); setTimeout(() => containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }}
          className="flex items-center gap-2 text-sm text-chakra-crown/50 hover:text-divine-gold transition-colors" data-testid="exit-rel-quiz-btn">
          <ChevronLeft className="w-4 h-4" /> Back to All Sections
        </button>

        <AnimatePresence mode="wait">
          <motion.div key={activeSection} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
            <div className="sacred-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-divine-gold/10 flex items-center justify-center shrink-0">
                  <activeGroup.IconComp className={`w-6 h-6 ${activeGroup.color}`} />
                </div>
                <div>
                  <h3 className={`font-heading text-lg ${activeGroup.color}`}>{activeGroup.name}</h3>
                  <p className="text-sm text-chakra-crown/40 font-heading">{activeGroup.subtitle}</p>
                  <p className="text-sm text-chakra-crown/60 mt-2 leading-relaxed">{activeGroup.description}</p>
                </div>
              </div>
            </div>

            {activeQuestions.map((q, idx) => (
              <div key={q.id} className="sacred-card" data-testid={`rel-question-${q.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-divine-gold/40 text-sm font-heading shrink-0 mt-0.5">Q{idx + 1}</span>
                  <div>
                    <p className="text-chakra-crown text-sm leading-relaxed">{q.question}</p>
                    <span className="text-chakra-crown/30 text-[10px]">{q.pillar} ({q.pillar_english})</span>
                  </div>
                </div>
                <QuestionInput question={q} answer={answers[q.id]} onChange={(a) => updateAnswer(q.id, a)} />
              </div>
            ))}

            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-chakra-crown/40">
                {activeQuestions.filter(q => answers[q.id]).length}/{activeQuestions.length} answered
              </span>
              <div className="flex gap-3">
                <button onClick={() => { setQuizView("overview"); setTimeout(() => containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-divine-gold/20 text-chakra-crown/60 hover:text-divine-gold hover:border-divine-gold/40 text-sm transition-all"
                  data-testid="exit-rel-quiz-bottom-btn">
                  Exit
                </button>
                <button onClick={() => saveSection(activeSection)} disabled={saving || !isSectionAnswered(activeGroup)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-divine-gold text-cosmic-void font-heading text-sm hover:bg-divine-gold-light disabled:opacity-50 transition-all"
                  data-testid="save-rel-section-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  SAVE SECTION
                </button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  // ====== PAIRED OVERVIEW ======
  return (
    <div className="space-y-4" ref={containerRef} data-testid="rel-diagnostic-paired">
      {/* Header */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl text-divine-gold">A. DIAGNOSTIC TESTS</h2>
            <span className="text-divine-gold font-heading text-sm">{totalCompletedCount}/{totalTestCount}</span>
          </div>
          <p className="text-chakra-crown/50 text-xs mt-1">12D Relationship Assessment</p>
          <Progress value={(totalCompletedCount / totalTestCount) * 100} className="h-1.5 mt-2" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl text-divine-gold">B. DIAGNOSTIC RESULTS</h2>
            {profilingComplete && (
              <button onClick={handleRetakeAll} disabled={saving}
                className="text-[10px] px-2 py-0.5 rounded border border-divine-gold/20 text-divine-gold hover:bg-divine-gold/10 transition-colors"
                data-testid="retake-all-profiling">
                Retake All
              </button>
            )}
          </div>
          <p className="text-chakra-crown/50 text-xs mt-1">Computed analysis from assessments</p>
          <Progress value={profilingComplete ? (totalCompletedCount / totalTestCount) * 100 : (totalCompletedCount / totalTestCount) * 100} className="h-1.5 mt-2" />
        </div>
      </div>

      {/* Coach Assessment Matrix — always visible in Awareness section */}
      <div
        className={`rounded-xl border p-4 sm:p-5 flex items-start gap-4 ${
          coachMatrixActive
            ? "border-emerald-500/30 bg-emerald-500/5"
            : "border-amber-500/30 bg-amber-500/5"
        }`}
        data-testid="awareness-coach-assessment-cta"
      >
          <ScrollText className={`w-5 h-5 mt-0.5 shrink-0 ${coachMatrixActive ? "text-emerald-300" : "text-amber-300"}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className={`font-heading text-sm sm:text-base ${coachMatrixActive ? "text-emerald-300" : "text-amber-300"}`}>
                Coach Assessment Matrix
              </p>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold uppercase tracking-wider ${
                coachMatrixActive
                  ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"
                  : "text-amber-300 border-amber-500/40 bg-amber-500/10"
              }`}>
                {coachMatrixActive ? "Active" : "Awaiting Activation"}
              </span>
            </div>
            <p className="text-chakra-crown/65 text-xs mt-1.5 leading-relaxed">
              {coachMatrixActive
                ? "Your coach has activated the Cosmic Foundation Diagnostic. Complete the 30-dimension matrix across Shiva, Shakti, Gunas, Arishadvargas & Pañca Koshas — your coach will review and refine each response."
                : "A deeper, coach-led 30-dimension Vedic diagnostic across Shiva, Shakti, Gunas, Arishadvargas & Pañca Koshas. Preview the assessment now — your coach can activate it for you when you're ready."}
            </p>
          </div>
          <button
            onClick={() => navigate("/coach-assessment-matrix")}
            data-testid="awareness-coach-assessment-cta-btn"
            className={`shrink-0 px-3 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
              coachMatrixActive
                ? "bg-emerald-500 text-cosmic-void hover:bg-emerald-400"
                : "bg-amber-500/90 text-cosmic-void hover:bg-amber-400"
            }`}
          >
            {coachMatrixActive ? "Open Matrix" : "Preview"}
          </button>
        </div>

      {/* Preliminary results indicator — visible while awaiting full section completion */}
      {isPreliminary && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-400/30 flex items-center gap-2" data-testid="preliminary-banner">
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          <p className="text-[11px] text-amber-200/85 leading-relaxed">
            <strong className="font-heading tracking-wide">PRELIMINARY RESULTS</strong> — your Trinity Gap, Perception Board, and scores below are computed on what you&apos;ve answered so far and refine automatically as you complete more sections.
          </p>
        </div>
      )}

      {/* Paired rows */}
      {QUESTION_GROUPS.map(group => {
        const isCompleted = group.isSelfAssessment
          ? (group.id === "vikara" ? vikaraCompleted : gunaCompleted)
          : (completedSections.includes(group.id) || (profilingComplete && !group.infoOnly && !group.isSelfAssessment));
        const Icon = group.IconComp;
        const selfQuestions = group.id === "vikara" ? vikaraQuestions : group.id === "guna" ? gunaQuestions : [];
        const questionCount = group.isSelfAssessment ? selfQuestions.length : group.questionIds.length;

        return (
          <div key={group.id} className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch" data-testid={`rel-paired-row-${group.id}`}>
            {/* LEFT: Test Card */}
            <div className={`p-4 rounded-xl border transition-all ${
              isCompleted ? "bg-emerald-500/5 border-emerald-500/20" : "bg-cosmic-light/5 border-divine-gold/10"
            }`} data-testid={`rel-test-card-${group.id}`}>
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isCompleted ? "bg-emerald-500/10" : "bg-divine-gold/10"}`}>
                  <Icon className={`w-5 h-5 ${isCompleted ? "text-emerald-400" : group.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-heading truncate ${isCompleted ? "text-emerald-400" : group.color}`}>{group.name}</p>
                    {isCompleted && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                  </div>
                  <p className="text-xs text-chakra-crown/40 font-heading">{group.subtitle}</p>
                  <p className="text-xs text-chakra-crown/50 mt-1.5 leading-relaxed line-clamp-2">{group.description}</p>
                  <span className="text-[10px] text-chakra-crown/30 mt-1 block">
                    {questionCount} question{questionCount > 1 ? "s" : ""}
                  </span>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <button onClick={() => {
                    setActiveSection(group.id);
                    setQuizView(group.isSelfAssessment ? `${group.id}-quiz` : "quiz");
                    setTimeout(() => containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
                  }}
                    className="p-1.5 rounded-lg hover:bg-divine-gold/10 transition-colors" data-testid={`rel-take-quiz-${group.id}`}>
                    <ChevronRight className={`w-4 h-4 ${isCompleted ? "text-emerald-400/40" : "text-divine-gold/40"}`} />
                  </button>
                  {isCompleted && (
                    <button onClick={() => {
                      if (group.id === "vikara") retakeVikaraAssessment();
                      else if (group.id === "guna") retakeGunaAssessment();
                      else handleRetakeSection(group.id);
                    }} disabled={saving}
                      className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border border-divine-gold/20 text-divine-gold hover:bg-divine-gold/10 transition-colors"
                      data-testid={`retake-section-${group.id}`}>
                      <RefreshCw className="w-2.5 h-2.5" /> Retake
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT: Collapsible Result Card */}
            {group.id === "pillars" && (
              <CollapsibleResult title="PILLAR-WISE TRIGGER BOARD" count={retakenSections.has("pillars") ? 0 : (awareness?.trigger_board?.length || 0)} countLabel="pillars" testId={`rel-result-card-${group.id}`} icon={Shield} iconColor="text-divine-gold">
                {retakenSections.has("pillars")
                  ? <p className="text-amber-400/60 text-sm text-center py-4">Retaking Pillar Matrix — save this section to refresh your Perception Board</p>
                  : <PillarTriggerBoard triggerBoard={awareness?.trigger_board} />}
              </CollapsibleResult>
            )}
            {group.id === "support" && (
              <CollapsibleResult title="SUPPORT NETWORK ANALYSIS" testId={`rel-result-card-${group.id}`} icon={Users} iconColor="text-sky-400">
                {retakenSections.has("support")
                  ? <p className="text-amber-400/60 text-sm text-center py-4">Retaking assessment — save to see updated results</p>
                  : <SupportNetworkResult analysis={awareness?.support_network_analysis} />}
              </CollapsibleResult>
            )}
            {group.id === "energy" && (
              <CollapsibleResult title="ENERGY POLARITY" testId={`rel-result-card-${group.id}`} icon={Scale} iconColor="text-pink-400">
                {retakenSections.has("energy")
                  ? <p className="text-amber-400/60 text-sm text-center py-4">Retaking assessment — save to see updated results</p>
                  : <EnergyResult energy={awareness?.energy} selfAssessment={awareness?.energy_self_assessment} />}
              </CollapsibleResult>
            )}
            {group.id === "vikara" && (
              <CollapsibleResult title="VIKARA INTENSITIES" count={6} countLabel="vikaras" testId={`rel-result-card-${group.id}`} icon={Flame} iconColor="text-orange-400">
                {retakenSections.size > 0
                  ? <p className="text-amber-400/60 text-sm text-center py-4">Retaking assessment — save the affected section(s) to refresh Vikara scores</p>
                  : <VikaraResult vikara={awareness?.vikara} selfAssessment={awareness?.vikara_self_assessment} />}
              </CollapsibleResult>
            )}
            {group.id === "guna" && (
              <CollapsibleResult title="GUNA DISTRIBUTION" count={3} countLabel="gunas" testId={`rel-result-card-${group.id}`} icon={Target} iconColor="text-teal-400">
                {retakenSections.size > 0
                  ? <p className="text-amber-400/60 text-sm text-center py-4">Retaking assessment — save the affected section(s) to refresh Guna scores</p>
                  : <GunaResult guna={awareness?.guna} selfAssessment={awareness?.guna_self_assessment} />}
              </CollapsibleResult>
            )}
            {group.id === "vision" && (
              <CollapsibleResult title="PRAYOJANA VISION" testId={`rel-result-card-${group.id}`} icon={Target} iconColor="text-emerald-400">
                {retakenSections.has("vision")
                  ? <p className="text-amber-400/60 text-sm text-center py-4">Retaking assessment — save to see updated results</p>
                  : <PrayojanaVisionResult answer={answers?.Q9} />}
              </CollapsibleResult>
            )}
          </div>
        );
      })}

      {/* PURUSHARTHAS · 5 PILLARS — paired row matching other diagnostic sections (left = pillars, right = snapshot) */}
      <PurusharthasAwarenessPair token={token} />

      {/* CIRCLE OF ORIGIN · 4 TRAITS — paired row (left = trait questions, right = classification + perception result) */}
      <CircleOfOriginAwarenessPair token={token} />
    </div>
  );
}
